import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { SortHelper } from '../../SortHelper';
import { Customer } from '../../interfaces/Customer';
import { customersActionCreators } from '../../store/customers';
import { Consultant } from '../../interfaces/Consultant';
import { ConsultantActionCreators } from '../../store/consultants';
import { dataActionCreators, ProjectAssigment } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { TokenHelper } from '../../TokenHelper';
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, InputOnChangeData, PopoverSurface, PopoverTrigger, SearchBox, SearchBoxChangeEvent, Skeleton, SkeletonItem, Table, TableBody, TableCell, TableCellActions, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Tag } from '@fluentui/react-components';
import { Popover } from '@fluentui/react-components';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { ConsultantDropdown } from '../EditorHelpers/ConsultantDropdown';
import { AddIcon, ArrowClockwiseIcon, CopySelectIcon, EditIcon, FilterIcon, TrashCanIcon, UploadIcon, useEditorStyles, useTabStyles } from '../Styles/Editor';
import { DownloadSPOLinkAndNav } from '../Tools/FileDownloadHelper';
import { SharePointSVG } from '../Resources/SharePointSVG';
import { useConsultants, useCustomers, useProjects, useProjectsAssigments, useTimeSheetEntries } from '../Tools/ObjectLoader';

export const ProjectsAssigmentsTab = () => {
    const { customers } = useCustomers();
    const { consultants } = useConsultants();
    const { projects } = useProjects();
    const { projectsAssigments } = useProjectsAssigments();
    const { timeSheetEntries } = useTimeSheetEntries();
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { t } = useTranslation();
    const [filter, setFilter] = React.useState<string | null>(null);
    const [filterConsultant, setFilterConsultant] = React.useState<Consultant | undefined>(undefined);
    const [filterCustomer, setFilterCustomer] = React.useState<Customer | undefined>(undefined);
    const [filteredProjectsAssigments, setFilteredProjectsAssigments] = React.useState<ProjectAssigment[] | undefined>(undefined);
    const styles = useEditorStyles();
    const stylesTab = useTabStyles();

    React.useEffect(() => {
        if (projectsAssigments != undefined && projects !== undefined) {
            let filtered = projectsAssigments;
            // Apply filter if any
            if (filterConsultant) {
                filtered = filtered
                    .filter(pa => {
                        if (pa.consultantid === filterConsultant.consultantid) {
                            return true;
                        }
                        return false;
                    });
            }
            if (filterCustomer) {
                filtered = filtered
                    .filter(pa => {
                        if (projects) {
                            const p = projects.find(x => x.projectid === pa.projectid);
                            if (p && p.customerid === filterCustomer.customerid) {
                                return true;
                            }
                        }
                        return false;
                    });
            }
            if (filter && filter.length != 0) {
                filtered = filtered
                    .filter(pa => pa.displayName && pa.displayName.toLowerCase().includes(filter.toLowerCase()));
            }
            setFilteredProjectsAssigments(filtered);
        }

    }, [projectsAssigments, projects, filterConsultant, filterCustomer, filter]);

    const MapSkeleton = (i: number) => <TableRow key={i}>
        <TableCell>
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
        </TableCell>
        <TableCell>
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
        </TableCell>
        <TableCell>
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
        </TableCell>
        <TableCell>
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
        </TableCell>
        <TableCell>
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
        </TableCell>
        <TableCell>
            <Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
        </TableCell>
    </TableRow>;

    const mapProjectAssigment = (pa: ProjectAssigment): JSX.Element => {
        const p = projects ? projects.find(e => e.projectid === pa.projectid) : undefined;
        const c = p && customers ? customers.find(e => e.customerid === p.customerid) : undefined;
        const co = consultants ? consultants.find(e => e.consultantid === pa.consultantid) : undefined;
        const ts = timeSheetEntries ? timeSheetEntries.find(tsTemp => pa.projectAssignmentid === tsTemp.projectAssignmentid) : undefined;
        return <TableRow key={pa.projectAssignmentid}>
            <TableCell>
                <TableCellLayout
                    description={pa.customerPrice.toLocaleString(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 3 }) + "/" + pa.unitOfTime}
                >
                    {c && c.displayName
                        ? <Button appearance="transparent" className={stylesTab.TestButton}
                            onClick={() => dispatch(customersActionCreators.requestCustomerQuickView(c))}>
                            {c.displayName ? c.displayName : "?"}
                        </Button>
                        : "?"
                    }
                </TableCellLayout>
            </TableCell>
            <TableCell>
                <TableCellLayout
                    description={p
                        ? <Button appearance="transparent" className={stylesTab.TestButton} size="small"
                            onClick={() => dispatch(dataActionCreators.requestProjectQuickView(p))}
                        >
                            {p.displayName ? "(" + p.displayName.trim() + ")" : "(?)"}
                        </Button>
                        : "(?)"}
                    appearance="primary"
                >
                    <Button appearance="transparent" className={stylesTab.TestButton}
                        onClick={() => dispatch(dataActionCreators.requestProjectAssigmentQuickView(pa))} >
                        {pa.displayName ? pa.displayName : "?"}
                    </Button>
                </TableCellLayout>
                <TableCellActions>
                    <Button
                        icon={<EditIcon />}
                        appearance="subtle"
                        title={t('Edit')} aria-label={t('Edit')}
                        onClick={() => dispatch(dataActionCreators.requestProjectAssigmentEdition(pa))}
                    />
                    <Button
                        icon={<UploadIcon />}
                        appearance="subtle"
                        aria-label="Upload"
                        onClick={() => dispatch(dataActionCreators.requestFileUploadProjectAssigment(pa))}
                    />
                    {pa.orderSharePointFileUniqueId !== null &&
                        <Button
                            icon={<SharePointSVG />}
                            appearance="subtle"
                            title={t('ViewInSharePoint')} aria-label={t('ViewInSharePoint')}
                            onClick={() => {
                                TokenHelper.GetToken(instance, teamsState)
                                    .then(token => {
                                        if (token !== null && pa.projectAssignmentid) {
                                            DownloadSPOLinkAndNav('api/ProjectsAssigments/' + pa.projectAssignmentid + "/DownloadSPOLink", token);
                                        }
                                    });
                            }}
                        />
                    }
                    {ts === undefined &&
                        <Button
                            icon={<TrashCanIcon />}
                            appearance="subtle"
                            aria-label="Delete"
                            onClick={() => dispatch(dataActionCreators.requestProjectAssigmentDeletion(pa))}
                        />
                    }
                    <Button icon={<CopySelectIcon />} appearance="subtle" title={t('Copy')} aria-label={t('Copy')}
                        onClick={() => {
                            const new_pa: ProjectAssigment = {
                                projectAssignmentid: null,
                                projectid: pa.projectid,
                                displayName: pa.displayName,
                                consultantid: pa.consultantid,
                                consultantCode: pa.consultantCode,
                                consultantPrice: pa.consultantPrice,
                                consultantVatcode: pa.consultantVatcode,
                                customerPrice: pa.customerPrice,
                                customerVatcode: pa.customerVatcode,
                                dateDebut: null,
                                dateFin: null,
                                customerSignedTimeSheetNeeded: pa.customerSignedTimeSheetNeeded,
                                po: pa.po,
                                description: pa.description,
                                poquantity: pa.poquantity,
                                unitOfTime: pa.unitOfTime,
                                orderSharePointFileUniqueId: null,
                                buyerItemIdentification: pa.buyerItemIdentification,
                                sellerItemIdentification: pa.sellerItemIdentification,
                                peppolAttachmentInvoiceName: pa.peppolAttachmentInvoiceName,
                                peppolAttachmentTsname: pa.peppolAttachmentTsname,
                                dateDebutTsencoding: null,
                                dateFinTsencoding: null,
                            };
                            dispatch(dataActionCreators.requestProjectAssigmentEdition(new_pa));
                        }} />
                </TableCellActions>
            </TableCell>
            <TableCell>
                <TableCellLayout
                    description={pa.consultantPrice.toLocaleString(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 3 }) + "/" + pa.unitOfTime}
                >
                    {co && co.displayName
                        ? <Button appearance="transparent" className={stylesTab.TestButton}
                            onClick={() => dispatch(ConsultantActionCreators.requestConslutantQuickView(co ? co : null))}>
                            {co.displayName ? co.displayName : "?"}
                        </Button>
                        : "?"
                    }
                </TableCellLayout>
            </TableCell>
            <TableCell>
                <TableCellLayout
                    description={(pa.dateDebutTsencoding ? new Date(pa.dateDebutTsencoding).toLocaleDateString() : "-") + " → " + (pa.dateFinTsencoding ? new Date(pa.dateFinTsencoding).toLocaleDateString() : "-")}
                >
                    {(pa.dateDebut ? new Date(pa.dateDebut).toLocaleDateString() : "-") + " → " + (pa.dateFin ? new Date(pa.dateFin).toLocaleDateString() : "-")}
                </TableCellLayout>
            </TableCell>
        </TableRow>;
    };

    const columns = [
        { columnKey: "Customers", label: t("Customer"), className: stylesTab.width17 },
        { columnKey: "ProjectProjectsAssigment", label: (t('ProjectsAssigment') + " (" + t('Project') + ")"), className: stylesTab.width17 },
        { columnKey: "Consultant", label: t('Consultant'), className: stylesTab.width17 },
        { columnKey: "PeriodSlice", label: t('PeriodSlice'), className: stylesTab.width15 },
    ];

    return <div className={stylesTab.rootSub}>
        <div className={stylesTab.MainBar}>
            <Popover positioning="below-start">
                <PopoverTrigger>
                    <Button icon={<FilterIcon />} shape="circular" title="Filter" />
                </PopoverTrigger>
                <PopoverSurface>
                    <div className={stylesTab.List}>
                        {t('Consultant')}
                        <ConsultantDropdown
                            currentConsultant={filterConsultant}
                            setConsultant={setFilterConsultant}
                        />
                        {t('Customer')}
                        <CustomerDropdown
                            currentCustomer={filterCustomer}
                            customers={customers ?? []}
                            setCustomer={setFilterCustomer} />
                    </div>
                </PopoverSurface>
            </Popover>
            {filterConsultant &&
                <Tag
                    dismissible
                    dismissIcon={{ "aria-label": "remove" }}
                    size="small"
                    onClick={() => { setFilterConsultant(undefined); }}
                    appearance="outline"
                >
                    {t('Consultant') + " = " + filterConsultant.displayName}
                </Tag>}
            {filterCustomer &&
                <Tag
                    dismissible
                    dismissIcon={{ "aria-label": "remove" }}
                    size="small"
                    onClick={() => { setFilterCustomer(undefined); }}
                    appearance="outline"
                >
                    {t('Customer') + " = " + filterCustomer.displayName}
                </Tag>}
            <SearchBox
                placeholder={t('Contains')}
                onChange={(e: SearchBoxChangeEvent, data: InputOnChangeData) => { setFilter(data.value); }}
                dismiss={{ onClick: () => { setFilter(null); } }}
                className={styles.fluid}
            />
            <div className={stylesTab.MarginLeftAuto}>
                <Button icon={<AddIcon />} shape="circular" title={t('Add')}
                    onClick={() => {
                        const pa: ProjectAssigment = {
                            projectAssignmentid: null,
                            projectid: null,
                            displayName: null,
                            consultantid: null,
                            consultantCode: null,
                            consultantPrice: 0,
                            consultantVatcode: 21,
                            customerPrice: 0,
                            customerVatcode: 21,
                            dateDebut: null,
                            dateFin: null,
                            customerSignedTimeSheetNeeded: false,
                            po: null,
                            description: null,
                            poquantity: null,
                            unitOfTime: "H",
                            orderSharePointFileUniqueId: null,
                            buyerItemIdentification: null,
                            sellerItemIdentification: null,
                            peppolAttachmentInvoiceName: null,
                            peppolAttachmentTsname: null,
                            dateDebutTsencoding: null,
                            dateFinTsencoding: null,
                        };
                        dispatch(dataActionCreators.requestProjectAssigmentEdition(pa));
                    }} />
                <Button icon={<ArrowClockwiseIcon />} shape="circular" title="Reload"
                    onClick={() => {
                        dispatch(dataActionCreators.ReloadProjectAssigment());
                    }} />
            </div>
        </div>
        <Accordion multiple collapsible defaultOpenItems="1">
            <AccordionItem value="1">
                <AccordionHeader><h2>{t('Ongoing')}</h2></AccordionHeader>
                <AccordionPanel>

                    <Table arial-label="Default table">
                        <TableHeader>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableHeaderCell key={column.columnKey} className={column.className}>
                                        {column.label}
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredProjectsAssigments === undefined
                                ? [...Array(10)].map((_, index) => MapSkeleton(index))
                                : filteredProjectsAssigments.filter(t => t.dateFin === null || (new Date(t.dateFin).getTime() >= Date.now()))
                                    .sort((a, b) => SortHelper.ProjectAssigmentByCustomerThenDisplayName(projects, customers, a, b))
                                    .map(p => mapProjectAssigment(p)
                                    )}
                        </TableBody>
                    </Table>
                </AccordionPanel>
            </AccordionItem>
            <AccordionItem value="2">
                <AccordionHeader><h2>{t('Obsolete')}</h2></AccordionHeader>
                <AccordionPanel>
                    <Table arial-label="Default table">
                        <TableHeader>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableHeaderCell key={column.columnKey} className={column.className}>
                                        {column.label}
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredProjectsAssigments === undefined
                                ? [...Array(10)].map((_, index) => MapSkeleton(index))
                                : filteredProjectsAssigments.filter(t => (t.dateFin && new Date(t.dateFin).getTime() < Date.now()))
                                    .sort((a, b) => SortHelper.ProjectAssigmentByCustomerThenDisplayName(projects, customers, a, b))
                                    .map(p => mapProjectAssigment(p)
                                    )}
                        </TableBody>
                    </Table>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    </div>;
};